import React from 'react'
import AntdModal from 'antd/es/modal'

import { getDesignMode } from 'src/design-mode/store'
import { useDesignMode } from 'src/design-mode/useDesignMode'

import { shadcnModalStatics, useShadcnModal } from './imperative'
import { Modal as ShadcnSeamModal } from './Modal.shadcn'
import { destroyAllShadcnModals } from './registry'
import type { ModalFuncProps, ModalProps } from '../antd-props'

export type { ModalProps }

/**
 * Compat seam for antd's `antd/es/modal` — and, with `../Tooltip`, one of the
 * two PORTAL surfaces put behind the design toggle in the same pass.
 *
 * A LEAF directory, not the `src/antd` barrel, on purpose (PRO-130): the
 * barrel's `export * from 'antd'` pulls antd/es/index.js — the whole component
 * barrel — into whatever chunk imports it. `update-design` ships a real seam at
 * this same path with this same shape, so a call site pointed here needs no
 * further change when that lands.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this renders antd's own Modal with
 * the props it was given: same element, same class names, same DOM. The only
 * difference from `export { default as Modal } from 'antd/es/modal'` is one
 * extra component in the React tree, which renders nothing of its own.
 * `../__tests__/modal-seam-parity.test.tsx` asserts that across a prop matrix,
 * and it is the test to keep green.
 *
 * ---------------------------------------------------------------------------
 * THE STATICS ARE THE INTERESTING HALF
 *
 * `Modal.confirm` (34 call sites) and `Modal.error` (2) are called from event
 * handlers, not from a render — so they cannot read a React hook and there is
 * no component to re-render. They dispatch on `getDesignMode()` at CALL time
 * instead: flip the toggle and the NEXT confirm switches, an already-open one
 * does not. That is the honest behaviour for something that mounts its own
 * React root, and it is why `destroyAll` below tears down BOTH backends rather
 * than the current one — a modal opened before the flip must still close.
 *
 * `warn` is antd's deprecated alias for `warning` and is carried for the same
 * reason `Button.__ANT_BUTTON` is: a shape that exists on antd's export and
 * not on the seam's is a call site that compiles today and breaks the day the
 * package goes.
 */
const ModalDispatcher = (props: ModalProps): JSX.Element => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamModal {...props} />

  return <AntdModal {...props} />
}

ModalDispatcher.displayName = 'Modal'

type StaticName = 'confirm' | 'info' | 'success' | 'error' | 'warning'

const dispatchStatic =
  (name: StaticName) =>
  (config: ModalFuncProps): ReturnType<typeof AntdModal.confirm> => {
    if (getDesignMode() === 'shadcn') {
      return shadcnModalStatics[name](config) as ReturnType<
        typeof AntdModal.confirm
      >
    }
    return AntdModal[name](config)
  }

const confirm = dispatchStatic('confirm')
const info = dispatchStatic('info')
const success = dispatchStatic('success')
const error = dispatchStatic('error')
const warning = dispatchStatic('warning')

/**
 * Both backends, always. A modal opened before the toggle was flipped is still
 * on screen after it, and `destroyAll()` means "close everything".
 */
const destroyAll = (): void => {
  destroyAllShadcnModals()
  AntdModal.destroyAll()
}

const useModal: typeof AntdModal.useModal = (...args) => {
  // Hooks cannot be called conditionally, so BOTH are created and the live one
  // is chosen after. antd's holder must still be rendered by the caller for
  // its half to work, which is exactly what the returned tuple carries.
  const antd = AntdModal.useModal(...args)
  const [shadcnInstance, shadcnHolder] = useShadcnModal()
  const designMode = useDesignMode()

  if (designMode === 'shadcn') {
    return [
      shadcnInstance as unknown as ReturnType<typeof AntdModal.useModal>[0],
      shadcnHolder,
    ]
  }
  return antd
}

export const Modal = Object.assign(ModalDispatcher, {
  confirm,
  info,
  success,
  error,
  warning,
  warn: warning,
  destroyAll,
  useModal,
  config: AntdModal.config,
})

export default Modal
